import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, AlertCircle, Loader2, Clock,
  Camera, FileText, CreditCard,
} from 'lucide-react';
import { Button } from '~/components/ui/button';
import { StatusCard } from '~/components/StatusCard';
import LinkReplaced from './LinkReplaced';
import { linkReplacedSchoolName } from './linkReplacedError';
import { WizardFormLayout } from '~/components/forms/WizardFormLayout';
import { PaymentErrorState } from './enrollment-packet/PaymentErrorState';
import {
  MediaReleaseStep,
  FieldTripReleaseStep,
  PolicyAcknowledgmentsStep,
  PaymentStep
} from './packet-steps';
import {
  useGetEnrollmentPacket,
  useSaveEnrollmentDraft,
  useSubmitEnrollmentPacket,
  useVerifyCheckout,
  useCreateCheckoutSession,
} from './enrollment-packet/useEnrollmentPacketData';

const RELEASE_STEPS = ['media', 'fieldtrip'];
const STEP_ICONS: Record<string, any> = {
  media: Camera,
  fieldtrip: Camera,
  policies: FileText,
  payment: CreditCard,
};

/** Build initial answers structure for a new packet */
function buildInitialAnswers(students: any[]) {
  const releases: Record<string, any> = {};
  students.forEach((student: any) => {
    releases[student.studentEnrollmentId] = {
      mediaRelease: { consent: null, signature: null },
      fieldTripRelease: { consent: null, transportConsent: null, signature: null },
    };
  });
  return {
    studentReleases: releases,
    policyAcknowledgments: {
      uniformPolicyViewed: false,
      tuitionFeeScheduleViewed: false,
      handbookViewed: false,
      handbookAcknowledged: false,
      tuitionAgreementAccepted: false,
    },
    paymentMethod: 'ach',
    signature: null,
    currentStep: 0,
  };
}

/** Check if a specific step is complete */
function checkStepComplete(stepData: any, answers: any, studentsList: any[]): boolean {
  if (!stepData || !answers) return false;

  if (stepData.type === 'release') {
    const studentId = studentsList[stepData.studentIndex]?.studentEnrollmentId;
    const releases = answers.studentReleases?.[studentId];
    if (!releases) return false;

    if (stepData.releaseType === 'media') {
      const media = releases.mediaRelease;
      return media?.consent !== null && !!media?.signature?.trim();
    }
    if (stepData.releaseType === 'fieldtrip') {
      const ft = releases.fieldTripRelease;
      return ft?.consent !== null && ft?.transportConsent !== null && !!ft?.signature?.trim();
    }
  }

  if (stepData.type === 'policies') {
    const acks = answers.policyAcknowledgments;
    return !!(
      acks?.uniformPolicyViewed &&
      acks?.tuitionFeeScheduleViewed &&
      acks?.handbookViewed &&
      acks?.handbookAcknowledged &&
      acks?.tuitionAgreementAccepted
    );
  }

  if (stepData.type === 'payment') {
    return !!answers.paymentMethod && !!answers.signature?.signedBy?.trim();
  }

  return false;
}

/** Find first incomplete step for resume logic */
function findFirstIncompleteStep(steps: any[], answers: any, studentsList: any[]): number {
  for (let i = 0; i < steps.length; i++) {
    if (!checkStepComplete(steps[i], answers, studentsList)) {
      return i;
    }
  }
  return steps.length - 1;
}

function EnrollmentPacket() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Form data - separate metadata (frozen) from answers (user responses)
  const [formMetadata, setFormMetadata] = useState<any>(null);
  const [formAnswers, setFormAnswers] = useState<any>(null);
  const [enrollment, setEnrollment] = useState<any>(null);
  const [paymentSettings, setPaymentSettings] = useState<any>(null);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [replacedBy, setReplacedBy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [redirectingToPayment, setRedirectingToPayment] = useState(false);
  const [creatingNewCheckout, setCreatingNewCheckout] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Payment state
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [checkoutPaymentMethod, setCheckoutPaymentMethod] = useState<string | null>(null);
  const [errorStatePaymentMethod, setErrorStatePaymentMethod] = useState<string | null>(null);

  const { fetchPacket } = useGetEnrollmentPacket(token as string);
  const { save: saveDraft } = useSaveEnrollmentDraft(token as string);
  const { submit: submitPacket } = useSubmitEnrollmentPacket(token as string);
  const { verify: verifyCheckout } = useVerifyCheckout(token as string);
  const { createSession: createCheckoutSession } = useCreateCheckoutSession(token as string);

  // Effective session id: prefer URL param, fall back to saved session id
  const effectiveSessionId = sessionId || formAnswers?.lastCheckoutSessionId;

  // Verify checkout session server-side
  useEffect(() => {
    if (!effectiveSessionId) return;

    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    async function verify() {
      try {
        const payload = await verifyCheckout(effectiveSessionId);
        if (cancelled) return;

        const errors = payload?.userErrors;
        if (errors && errors.length > 0) {
          setError(errors[0].message);
          setPaymentStatus('failed');
          return;
        }

        const status = payload?.paymentStatus || 'failed';
        setPaymentStatus(status);
        setCheckoutStatus(payload?.checkoutStatus ?? null);
        setCheckoutUrl(payload?.checkoutUrl ?? null);
        setCheckoutPaymentMethod(payload?.checkoutPaymentMethod ?? null);

        // Retry briefly for ACH processing
        if (status === 'processing' && attempts < 3) {
          attempts += 1;
          retryTimeout = window.setTimeout(() => {
            if (!cancelled) verify();
          }, 1500);
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err.message);
        setPaymentStatus('failed');
      }
    }

    verify();
    return () => {
      cancelled = true;
      if (retryTimeout) window.clearTimeout(retryTimeout);
    };
  }, [effectiveSessionId, token, verifyCheckout]);

  // Derived data
  const students = useMemo(() => formMetadata?.students || [], [formMetadata?.students]);
  const policyDocuments = formMetadata?.policyDocuments || {};
  const invoiceSummary = formMetadata?.invoiceSummary || [];

  // Build flat step list dynamically based on students
  const allSteps = useMemo(() => {
    const steps: any[] = [];
    students.forEach((student: any, idx: number) => {
      RELEASE_STEPS.forEach(releaseType => {
        steps.push({
          id: `${releaseType}-${idx}`,
          type: 'release',
          releaseType,
          studentIndex: idx,
          label: releaseType === 'media' ? 'Media' : 'Field Trip',
          studentName: student.firstName,
          icon: STEP_ICONS[releaseType],
        });
      });
    });
    steps.push({ id: 'policies', type: 'policies', label: 'Policies', icon: FileText });
    steps.push({ id: 'payment', type: 'payment', label: 'Payment', icon: CreditCard });
    return steps;
  }, [students]);

  const currentStepData = allSteps[currentStep];
  const isLastStep = currentStep === allSteps.length - 1;
  const isFirstStep = currentStep === 0;

  // Load packet on mount
  useEffect(() => {
    async function loadPacket() {
      try {
        const result = await fetchPacket();
        if (!result) return;
        const { familyEnrollment, userErrors } = result;
        const metadata = result.formMetadata as any;
        const answers = result.formAnswers as any;
        const settings = result.paymentSettings as any;

        if (userErrors && userErrors.length > 0) {
          setError(userErrors[0].message);
          setLoading(false);
          return;
        }

        setEnrollment(familyEnrollment);
        if (familyEnrollment?.isLocked) setSigned(true);

        // Build formMetadata from live fields instead of frozen metadata_json
        const computedMetadata = {
          enrollmentSnapshot: {
            familyName: result.familyName || metadata?.enrollmentSnapshot?.familyName,
            schoolYearName: result.schoolYearName || metadata?.enrollmentSnapshot?.schoolYearName,
          },
          students: result.packetStudents || metadata?.students || [],
          invoiceSummary: result.invoiceSummary || metadata?.invoiceSummary || [],
          policyDocuments: result.policyDocuments || metadata?.policyDocuments || {},
        };
        setFormMetadata(computedMetadata);

        const effectiveStudents = computedMetadata.students;
        const effectiveAnswers = answers || buildInitialAnswers(effectiveStudents);
        setFormAnswers(effectiveAnswers);
        setPaymentSettings(settings);

        // Find first incomplete step for resume
        const studentsList = effectiveStudents;
        const computedSteps: any[] = [];
        studentsList.forEach((_: any, idx: number) => {
          RELEASE_STEPS.forEach(releaseType => {
            computedSteps.push({ type: 'release', releaseType, studentIndex: idx });
          });
        });
        computedSteps.push({ type: 'policies' });
        computedSteps.push({ type: 'payment' });

        const firstIncomplete = findFirstIncompleteStep(computedSteps, effectiveAnswers, studentsList);
        const savedStep = answers?.currentStep || 0;
        const validatedStep = Math.min(savedStep, firstIncomplete);
        if (validatedStep > 0) setCurrentStep(validatedStep);

        // Set default payment method
        if (settings?.acceptOnlinePayments && !effectiveAnswers?.paymentMethod) {
          setFormAnswers((prev: any) => ({ ...prev, paymentMethod: 'ach' }));
        }

        setLoading(false);
      } catch (err: any) {
        const schoolName = linkReplacedSchoolName(err);
        if (schoolName) setReplacedBy(schoolName);
        setError(err.message);
        setLoading(false);
      }
    }
    loadPacket();
  }, [token, fetchPacket]);

  // Answer update helpers
  const updateStudentRelease = useCallback((studentId: string, releaseType: string, updates: any) => {
    setFormAnswers((prev: any) => ({
      ...prev,
      studentReleases: {
        ...prev.studentReleases,
        [studentId]: {
          ...prev.studentReleases[studentId],
          [releaseType]: {
            ...prev.studentReleases[studentId]?.[releaseType],
            ...updates,
          },
        },
      },
    }));
  }, []);

  const updatePolicyAcknowledgments = useCallback((data: any) => {
    setFormAnswers((prev: any) => ({ ...prev, policyAcknowledgments: data }));
  }, []);

  const updatePaymentMethod = useCallback((method: string) => {
    setFormAnswers((prev: any) => ({ ...prev, paymentMethod: method }));
  }, []);

  const updateSignature = useCallback((sig: any) => {
    setFormAnswers((prev: any) => ({ ...prev, signature: sig }));
  }, []);

  // Save draft on step navigation
  const saveStep = useCallback(async (stepIndex: number) => {
    if (!formAnswers || signed) return;
    try {
      await saveDraft(formAnswers, stepIndex);
    } catch (err) {
      console.error('Save failed:', err);
    }
  }, [formAnswers, saveDraft, signed]);

  // Current step completion
  const currentStepComplete = useMemo(
    () => checkStepComplete(currentStepData, formAnswers, students),
    [currentStepData, formAnswers, students]
  );

  // Navigation
  const nextStep = useCallback(async () => {
    if (currentStep < allSteps.length - 1) {
      const newStep = currentStep + 1;
      await saveStep(newStep);
      setCurrentStep(newStep);
      setFormAnswers((prev: any) => ({ ...prev, currentStep: newStep }));
    }
  }, [currentStep, allSteps.length, saveStep]);

  const prevStep = useCallback(async () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      await saveStep(newStep);
      setCurrentStep(newStep);
    }
  }, [currentStep, saveStep]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    if (!currentStepComplete) return;
    setSubmitting(true);
    try {
      const result = await submitPacket(formAnswers, formAnswers.paymentMethod);
      if (!result) return;

      const { userErrors, requiresPayment, checkoutUrl } = result;
      if (userErrors && userErrors.length > 0) {
        setError(userErrors[0].message);
        setSubmitting(false);
        return;
      }

      if (requiresPayment && checkoutUrl) {
        setRedirectingToPayment(true);
        window.location.href = checkoutUrl;
        return;
      }

      if (!requiresPayment) setSigned(true);
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  }, [formAnswers, currentStepComplete, submitPacket]);

  // New payment session for expired/failed checkout
  const handleStartNewPayment = useCallback(async () => {
    setCreatingNewCheckout(true);
    setError(null);

    const selectedPaymentMethod = errorStatePaymentMethod || formAnswers?.paymentMethod || 'ach';
    try {
      const result = await createCheckoutSession(selectedPaymentMethod);
      if (!result) return;

      const { checkoutUrl: newCheckoutUrl, userErrors } = result;
      if (userErrors && userErrors.length > 0) {
        setError(userErrors[0].message);
        setCreatingNewCheckout(false);
        return;
      }

      if (newCheckoutUrl) {
        window.location.href = newCheckoutUrl;
      } else {
        setError('Failed to create payment session');
        setCreatingNewCheckout(false);
      }
    } catch (err: any) {
      setError(err.message);
      setCreatingNewCheckout(false);
    }
  }, [formAnswers, errorStatePaymentMethod, createCheckoutSession]);

  // Grouped stepper steps for display (one per student + policies + payment)
  const stepperSteps = useMemo(() => {
    if (allSteps.length === 0) {
      return [{ id: 'loading', label: 'Loading...', icon: FileText, stepIndex: 0 }];
    }

    const grouped: any[] = [];
    students.forEach((student: any, idx: number) => {
      const firstReleaseStepIndex = allSteps.findIndex((s: any) => s.type === 'release' && s.studentIndex === idx);
      if (firstReleaseStepIndex >= 0) {
        grouped.push({
          id: `student-${idx}`,
          label: student.firstName,
          icon: Camera,
          stepIndex: firstReleaseStepIndex,
        });
      }
    });

    const policyStepIndex = allSteps.findIndex((s: any) => s.type === 'policies');
    if (policyStepIndex >= 0) {
      grouped.push({ id: 'policies', label: 'Policies', icon: FileText, stepIndex: policyStepIndex });
    }

    const paymentStepIndex = allSteps.findIndex((s: any) => s.type === 'payment');
    if (paymentStepIndex >= 0) {
      grouped.push({ id: 'payment', label: 'Payment', icon: CreditCard, stepIndex: paymentStepIndex });
    }

    return grouped.length > 0 ? grouped : [{ id: 'loading', label: 'Loading...', icon: FileText, stepIndex: 0 }];
  }, [students, allSteps]);

  // Map flat step index to grouped stepper index
  const currentGroupedStepIndex = useMemo(() => {
    for (let i = stepperSteps.length - 1; i >= 0; i--) {
      if (currentStep >= stepperSteps[i].stepIndex) return i;
    }
    return 0;
  }, [currentStep, stepperSteps]);

  // --- Early returns for status screens ---

  if (replacedBy) return <LinkReplaced schoolName={replacedBy} />;

  if (error) {
    return (
      <StatusCard
        icon={<AlertCircle className="h-5 w-5" />}
        title="Unable to Load Packet"
        titleClassName="text-destructive"
        footer={
          <Button variant="outline" onClick={() => { setError(null); window.location.reload(); }}>
            Try Again
          </Button>
        }
      >
        <p className="text-muted-foreground">{error}</p>
      </StatusCard>
    );
  }

  if (loading || !formMetadata || students.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your enrollment packet...</p>
        </div>
      </div>
    );
  }

  // Payment verification in progress
  if (effectiveSessionId && !paymentStatus && !error) {
    return (
      <StatusCard
        icon={<Loader2 className="h-5 w-5 animate-spin" />}
        title="Verifying Payment..."
      >
        <p className="text-muted-foreground">
          Please wait while we confirm your payment status.
        </p>
      </StatusCard>
    );
  }

  // Payment status screens
  if (effectiveSessionId && paymentStatus) {
    if (paymentStatus === 'paid') {
      return (
        <StatusCard
          icon={<CheckCircle className="h-5 w-5" />}
          title="Payment Successful!"
          titleClassName="text-green-600"
        >
          <p className="text-muted-foreground">
            Thank you! Your payment has been processed and your enrollment is now complete.
          </p>
          <p className="text-sm text-muted-foreground">
            You can safely close this tab. If you need to view your enrollment status later,
            you can return to this page using the link from your enrollment email.
          </p>
        </StatusCard>
      );
    }
    if (paymentStatus === 'processing') {
      return (
        <StatusCard
          icon={<Clock className="h-5 w-5" />}
          title="Payment Processing"
          titleClassName="text-blue-600"
        >
          <p className="text-muted-foreground">
            Your enrollment packet has been submitted! Your bank transfer is now being processed.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>What happens next:</strong>
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
              <li>ACH transfers typically take 1-4 business days to clear</li>
              <li>You'll receive a confirmation email when the payment completes</li>
              <li>Your enrollment will be finalized once payment is confirmed</li>
            </ul>
          </div>
          <p className="text-sm text-muted-foreground">
            You can safely close this tab. Return to this page anytime using the link
            from your enrollment email to check on your payment status.
          </p>
        </StatusCard>
      );
    }
    if (paymentStatus === 'not_found') {
      return (
        <StatusCard
          icon={<AlertCircle className="h-5 w-5" />}
          title="Unable to Verify Payment"
          titleClassName="text-destructive"
          footer={
            <Button onClick={() => navigate('/app')}>
              Contact School
            </Button>
          }
        >
          <p className="text-muted-foreground">
            We couldn't verify your payment status. This may be a temporary issue,
            or there may have been a problem with the checkout process.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Please contact the school office</strong> to verify your enrollment
              status before attempting another payment.
            </p>
          </div>
        </StatusCard>
      );
    }
    if (paymentStatus === 'failed' || paymentStatus === 'unpaid') {
      return (
        <PaymentErrorState
          paymentStatus={paymentStatus}
          checkoutStatus={checkoutStatus}
          checkoutUrl={checkoutUrl}
          invoiceSummary={invoiceSummary}
          paymentSettings={paymentSettings}
          currentPaymentMethod={errorStatePaymentMethod || checkoutPaymentMethod || formAnswers?.paymentMethod || 'ach'}
          originalPaymentMethod={checkoutPaymentMethod || formAnswers?.paymentMethod || 'ach'}
          onPaymentMethodChange={setErrorStatePaymentMethod}
          onStartNewPayment={handleStartNewPayment}
          creatingNewCheckout={creatingNewCheckout}
          error={error}
        />
      );
    }
  }

  if (redirectingToPayment) {
    return (
      <StatusCard
        icon={<Loader2 className="h-5 w-5 animate-spin" />}
        title="Redirecting to Payment..."
      >
        <p className="text-muted-foreground">
          Please wait while we redirect you to our secure payment page.
        </p>
      </StatusCard>
    );
  }

  if (signed) {
    return (
      <StatusCard
        icon={<CheckCircle className="h-5 w-5" />}
        title="Enrollment Submitted!"
        titleClassName="text-green-600"
        footer={
          <Button variant="outline" onClick={() => navigate('/app')}>
            Done
          </Button>
        }
      >
        <p className="text-muted-foreground">
          Thank you for completing your enrollment for the {enrollment?.family?.name}.
          You will receive payment information shortly.
        </p>
      </StatusCard>
    );
  }

  // Helper for current student release data
  const getCurrentStudentReleases = () => {
    if (!currentStepData || currentStepData.type !== 'release') return null;
    const studentId = students[currentStepData.studentIndex]?.studentEnrollmentId;
    return formAnswers?.studentReleases?.[studentId];
  };

  return (
    <WizardFormLayout
      title="Enrollment Packet"
      subtitle={`${formMetadata?.enrollmentSnapshot?.familyName} - ${formMetadata?.enrollmentSnapshot?.schoolYearName}`}
      steps={stepperSteps}
      currentStep={currentGroupedStepIndex}
      onStepClick={(idx: number) => {
        if (idx <= currentGroupedStepIndex) {
          setCurrentStep(stepperSteps[idx].stepIndex);
        }
      }}
      canProceed={currentStepComplete}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onNext={nextStep}
      onPrev={prevStep}
      submitLabel="Sign & Submit"
      onSubmit={handleSubmit}
      submitting={submitting}
      prevLabel="Back"
      footerExtra={
        currentStepData?.type === 'release' ? (
          <div className="text-sm text-muted-foreground">
            {students[currentStepData.studentIndex]?.firstName}:{' '}
            {currentStepData.releaseType === 'media' && 'Media Release'}
            {currentStepData.releaseType === 'fieldtrip' && 'Field Trip Release'}
          </div>
        ) : undefined
      }
    >
      {currentStepData?.type === 'release' && currentStepData.releaseType === 'media' && students[currentStepData.studentIndex] && (
        <MediaReleaseStep
          student={students[currentStepData.studentIndex]}
          release={getCurrentStudentReleases()?.mediaRelease || {}}
          onReleaseChange={(data: any) => updateStudentRelease(
            students[currentStepData.studentIndex].studentEnrollmentId,
            'mediaRelease',
            data
          )}
        />
      )}

      {currentStepData?.type === 'release' && currentStepData.releaseType === 'fieldtrip' && students[currentStepData.studentIndex] && (
        <FieldTripReleaseStep
          student={students[currentStepData.studentIndex]}
          release={getCurrentStudentReleases()?.fieldTripRelease || {}}
          onReleaseChange={(data: any) => updateStudentRelease(
            students[currentStepData.studentIndex].studentEnrollmentId,
            'fieldTripRelease',
            data
          )}
        />
      )}

      {currentStepData?.type === 'policies' && (
        <PolicyAcknowledgmentsStep
          policyDocuments={policyDocuments}
          invoiceSummary={invoiceSummary}
          acknowledgments={formAnswers?.policyAcknowledgments || {}}
          onAcknowledgmentChange={updatePolicyAcknowledgments}
        />
      )}

      {currentStepData?.type === 'payment' && (
        <PaymentStep
          invoiceSummary={invoiceSummary}
          paymentMethod={formAnswers?.paymentMethod}
          onPaymentMethodChange={updatePaymentMethod}
          signature={formAnswers?.signature}
          onSignatureChange={updateSignature}
          paymentSettings={paymentSettings}
        />
      )}

      {!currentStepData && (
        <div className="text-center py-8 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading form data...</p>
        </div>
      )}
    </WizardFormLayout>
  );
}

export default EnrollmentPacket;
